/**
 * Leviathan - Quote Form Page
 * Quasar Prime LLC
 *
 * Create and edit sales quotes
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  ArrowLeft,
  Save,
  Loader2,
  Plus,
  Trash2,
  Building2,
  User,
  Search,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Lead {
  id: string;
  leadNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
  primaryContactFirstName: string | null;
  primaryContactLastName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  unitPrice: number;
  unitOfMeasure: string;
}

interface QuoteItem {
  id?: string;
  productId: string | null;
  description: string;
  sku: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPercent: number;
  lineTotal: number;
  notes: string;
}

interface QuoteFormData {
  leadId: string | null;
  customerId: string | null;
  title: string;
  description: string;
  validUntil: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  discountType: string;
  discountValue: number;
  taxRate: number;
  deliveryFee: number;
  paymentTerms: string;
  deliveryTerms: string;
  specialTerms: string;
  notes: string;
  internalNotes: string;
  items: QuoteItem[];
}

const defaultItem: QuoteItem = {
  productId: null,
  description: '',
  sku: '',
  quantity: 1,
  unit: 'GAL',
  unitPrice: 0,
  discountPercent: 0,
  lineTotal: 0,
  notes: '',
};

const defaultFormData: QuoteFormData = {
  leadId: null,
  customerId: null,
  title: '',
  description: '',
  validUntil: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  deliveryAddress: '',
  deliveryCity: '',
  deliveryState: '',
  deliveryZip: '',
  discountType: 'PERCENT',
  discountValue: 0,
  taxRate: 0,
  deliveryFee: 0,
  paymentTerms: 'NET 30',
  deliveryTerms: '',
  specialTerms: '',
  notes: '',
  internalNotes: '',
  items: [{ ...defaultItem }],
};

export default function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  const [entityType, setEntityType] = useState<'lead' | 'customer'>('lead');
  const [entitySearch, setEntitySearch] = useState('');
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [formData, setFormData] = useState<QuoteFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get default date (30 days from now)
  useEffect(() => {
    if (!isEdit) {
      const defaultValidUntil = new Date();
      defaultValidUntil.setDate(defaultValidUntil.getDate() + 30);
      const validUntilStr = defaultValidUntil.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        validUntil: validUntilStr || '',
      }));

      // Pre-populate from URL params
      const leadId = searchParams.get('leadId');
      const customerId = searchParams.get('customerId');
      if (leadId) {
        setEntityType('lead');
        setFormData(prev => ({ ...prev, leadId }));
      } else if (customerId) {
        setEntityType('customer');
        setFormData(prev => ({ ...prev, customerId }));
      }
    }
  }, [isEdit, searchParams]);

  // Fetch existing quote for edit
  const { isLoading: isLoadingQuote } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch quote');
      return res.json();
    },
    enabled: isEdit,
    // @ts-expect-error - onSuccess is valid in react-query v4
    onSuccess: (data: Record<string, unknown>) => {
      setFormData({
        leadId: data.leadId as string | null,
        customerId: data.customerId as string | null,
        title: (data.title as string) || '',
        description: (data.description as string) || '',
        validUntil: data.validUntil ? (data.validUntil as string).split('T')[0] || '' : '',
        contactFirstName: (data.contactFirstName as string) || '',
        contactLastName: (data.contactLastName as string) || '',
        contactEmail: (data.contactEmail as string) || '',
        contactPhone: (data.contactPhone as string) || '',
        deliveryAddress: (data.deliveryAddress as string) || '',
        deliveryCity: (data.deliveryCity as string) || '',
        deliveryState: (data.deliveryState as string) || '',
        deliveryZip: (data.deliveryZip as string) || '',
        discountType: (data.discountType as string) || 'PERCENT',
        discountValue: (data.discountValue as number) || 0,
        taxRate: (data.taxRate as number) || 0,
        deliveryFee: (data.deliveryFee as number) || 0,
        paymentTerms: (data.paymentTerms as string) || '',
        deliveryTerms: (data.deliveryTerms as string) || '',
        specialTerms: (data.specialTerms as string) || '',
        notes: (data.notes as string) || '',
        internalNotes: (data.internalNotes as string) || '',
        items: ((data.items as QuoteItem[]) || []).map(item => ({
          id: item.id,
          productId: item.productId,
          description: item.description,
          sku: item.sku || '',
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          lineTotal: item.lineTotal,
          notes: item.notes || '',
        })),
      });
      setEntityType(data.leadId ? 'lead' : 'customer');
    },
  });

  // Fetch leads for selection
  const { data: leadsData } = useQuery({
    queryKey: ['leads-search', entitySearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: '20',
        search: entitySearch,
      });
      const res = await fetch(`${API_URL}/api/leads?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch leads');
      return res.json();
    },
    enabled: entityType === 'lead' && showEntityDropdown,
  });

  // Fetch customers for selection
  const { data: customersData } = useQuery({
    queryKey: ['customers-search', entitySearch],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: '20',
        search: entitySearch,
      });
      const res = await fetch(`${API_URL}/api/customers?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch customers');
      return res.json();
    },
    enabled: entityType === 'customer' && showEntityDropdown,
  });

  // Fetch selected lead details
  const { data: selectedLead } = useQuery<Lead>({
    queryKey: ['lead', formData.leadId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/${formData.leadId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lead');
      return res.json();
    },
    enabled: Boolean(formData.leadId),
  });

  // Fetch selected customer details
  const { data: selectedCustomer } = useQuery<Customer>({
    queryKey: ['customer', formData.customerId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/customers/${formData.customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch customer');
      return res.json();
    },
    enabled: Boolean(formData.customerId),
  });

  // Fetch products for line items
  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/inventory?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
  });

  const products: Product[] = productsData?.items || [];
  const leads: Lead[] = leadsData?.items || [];
  const customers: Customer[] = customersData?.items || [];

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: QuoteFormData) => {
      const url = isEdit ? `${API_URL}/api/quotes/${id}` : `${API_URL}/api/quotes`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/sales/quotes/${data.id}`);
    },
  });

  // Auto-populate contact info from selected entity
  useEffect(() => {
    if (selectedLead && !isEdit) {
      setFormData(prev => ({
        ...prev,
        contactFirstName: selectedLead.contactFirstName || '',
        contactLastName: selectedLead.contactLastName || '',
        contactEmail: selectedLead.contactEmail || '',
        contactPhone: selectedLead.contactPhone || '',
        deliveryAddress: selectedLead.address || '',
        deliveryCity: selectedLead.city || '',
        deliveryState: selectedLead.state || '',
        deliveryZip: selectedLead.zip || '',
      }));
    }
  }, [selectedLead, isEdit]);

  useEffect(() => {
    if (selectedCustomer && !isEdit) {
      setFormData(prev => ({
        ...prev,
        // The old code read `selectedCustomer.contactName`, which is not a field
        // the customers API returns — the prefill from a customer had never
        // worked. Found while splitting the name; now reads the real fields.
        contactFirstName: selectedCustomer.primaryContactFirstName || '',
        contactLastName: selectedCustomer.primaryContactLastName || '',
        contactEmail: selectedCustomer.email || '',
        contactPhone: selectedCustomer.phone || '',
        deliveryAddress: selectedCustomer.address || '',
        deliveryCity: selectedCustomer.city || '',
        deliveryState: selectedCustomer.state || '',
        deliveryZip: selectedCustomer.zip || '',
      }));
    }
  }, [selectedCustomer, isEdit]);

  const handleSelectLead = (lead: Lead) => {
    setFormData(prev => ({
      ...prev,
      leadId: lead.id,
      customerId: null,
    }));
    setShowEntityDropdown(false);
    setEntitySearch('');
  };

  const handleSelectCustomer = (customer: Customer) => {
    setFormData(prev => ({
      ...prev,
      customerId: customer.id,
      leadId: null,
    }));
    setShowEntityDropdown(false);
    setEntitySearch('');
  };

  const calculateLineTotal = (item: QuoteItem) => {
    const gross = item.quantity * item.unitPrice;
    const discount = gross * (item.discountPercent / 100);
    return gross - discount;
  };

  const handleItemChange = (index: number, field: keyof QuoteItem, value: unknown) => {
    const newItems = [...formData.items];
    const currentItem = newItems[index];
    if (!currentItem) return;

    newItems[index] = { ...currentItem, [field]: value };

    // Auto-calculate line total
    const updatedItem = newItems[index];
    if (updatedItem) {
      updatedItem.lineTotal = calculateLineTotal(updatedItem);
    }

    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const newItems = [...formData.items];
      const currentItem = newItems[index];
      if (!currentItem) return;

      newItems[index] = {
        ...currentItem,
        productId,
        description: product.name,
        sku: product.sku,
        unitPrice: product.unitPrice,
        unit: product.unitOfMeasure || 'GAL',
      };
      const updatedItem = newItems[index];
      if (updatedItem) {
        updatedItem.lineTotal = calculateLineTotal(updatedItem);
      }
      setFormData(prev => ({ ...prev, items: newItems }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...defaultItem }],
    }));
  };

  const removeItem = (index: number) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
  const discountAmount = formData.discountType === 'PERCENT'
    ? subtotal * (formData.discountValue / 100)
    : formData.discountValue;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (formData.taxRate / 100);
  const total = taxableAmount + taxAmount + formData.deliveryFee;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.leadId && !formData.customerId) {
      newErrors.entity = 'Please select a lead or customer';
    }

    if (!formData.validUntil) {
      newErrors.validUntil = 'Valid until date is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one line item is required';
    }

    const emptyItems = formData.items.filter(item => !item.description || item.quantity <= 0);
    if (emptyItems.length > 0) {
      newErrors.items = 'All line items must have a description and quantity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    saveMutation.mutate(formData);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isEdit && isLoadingQuote) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/sales/quotes"
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">
            {isEdit ? 'Edit Quote' : 'New Quote'}
          </h1>
          <p className="text-slate-400">
            {isEdit ? 'Update quote details' : 'Create a new sales quote'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="col-span-2 space-y-6">
            {/* Entity Selection */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Quote For
              </h3>

              {/* Entity Type Toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setEntityType('lead');
                    setFormData(prev => ({ ...prev, customerId: null }));
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    entityType === 'lead'
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  Lead
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEntityType('customer');
                    setFormData(prev => ({ ...prev, leadId: null }));
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    entityType === 'customer'
                      ? 'bg-amber-500 text-slate-900'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Customer
                </button>
              </div>

              {/* Entity Search */}
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={entitySearch}
                    onChange={(e) => setEntitySearch(e.target.value)}
                    onFocus={() => setShowEntityDropdown(true)}
                    placeholder={`Search ${entityType === 'lead' ? 'leads' : 'customers'}...`}
                    className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>

                {/* Dropdown */}
                {showEntityDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {entityType === 'lead' ? (
                      leads.length > 0 ? (
                        leads.map(lead => (
                          <button
                            key={lead.id}
                            type="button"
                            onClick={() => handleSelectLead(lead)}
                            className="w-full px-4 py-2 text-left hover:bg-slate-700 text-slate-100"
                          >
                            <div className="font-medium">{lead.companyName}</div>
                            <div className="text-sm text-slate-400">{lead.leadNumber}</div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-slate-400">No leads found</div>
                      )
                    ) : (
                      customers.length > 0 ? (
                        customers.map(customer => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => handleSelectCustomer(customer)}
                            className="w-full px-4 py-2 text-left hover:bg-slate-700 text-slate-100"
                          >
                            <div className="font-medium">{customer.name}</div>
                            <div className="text-sm text-slate-400">{customer.customerNumber}</div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-slate-400">No customers found</div>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Selected Entity Display */}
              {(formData.leadId || formData.customerId) && (
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  {selectedLead && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-amber-400" />
                          <span className="font-medium text-slate-100">{selectedLead.companyName}</span>
                        </div>
                        <div className="text-sm text-slate-400 mt-1">{selectedLead.leadNumber}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, leadId: null }))}
                        className="text-slate-500 hover:text-slate-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {selectedCustomer && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-amber-400" />
                          <span className="font-medium text-slate-100">{selectedCustomer.name}</span>
                        </div>
                        <div className="text-sm text-slate-400 mt-1">{selectedCustomer.customerNumber}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, customerId: null }))}
                        className="text-slate-500 hover:text-slate-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {errors.entity && (
                <p className="text-red-400 text-sm mt-2">{errors.entity}</p>
              )}
            </div>

            {/* Quote Details */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Quote Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Monthly Fuel Supply"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Valid Until *
                  </label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                  {errors.validUntil && (
                    <p className="text-red-400 text-sm mt-1">{errors.validUntil}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Payment Terms
                  </label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  >
                    <option value="">Select terms...</option>
                    <option value="DUE_ON_RECEIPT">Due on Receipt</option>
                    <option value="NET 10">NET 10</option>
                    <option value="NET 15">NET 15</option>
                    <option value="NET 30">NET 30</option>
                    <option value="NET 45">NET 45</option>
                    <option value="NET 60">NET 60</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    placeholder="Brief description of the quote..."
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Contact Information
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Contact First Name
                  </label>
                  <input
                    type="text"
                    value={formData.contactFirstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactFirstName: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Contact Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.contactLastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactLastName: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Delivery Address
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryCity}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryCity: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryState}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryState: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryZip}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryZip: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100">Line Items</h3>
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>

              {errors.items && (
                <p className="text-red-400 text-sm mb-4">{errors.items}</p>
              )}

              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-4">
                        <label className="block text-xs text-slate-500 mb-1">Product</label>
                        <select
                          value={item.productId || ''}
                          onChange={(e) => handleProductSelect(index, e.target.value)}
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        >
                          <option value="">Select product...</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.sku})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <label className="block text-xs text-slate-500 mb-1">Description *</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-xs text-slate-500 mb-1">Qty *</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-xs text-slate-500 mb-1">Unit</label>
                        <select
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        >
                          <option value="GAL">GAL</option>
                          <option value="LB">LB</option>
                          <option value="EA">EA</option>
                          <option value="TON">TON</option>
                        </select>
                      </div>
                      <div className="col-span-1">
                        <label className="block text-xs text-slate-500 mb-1">Price</label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="block text-xs text-slate-500 mb-1">Disc %</label>
                        <input
                          type="number"
                          value={item.discountPercent}
                          onChange={(e) => handleItemChange(index, 'discountPercent', parseFloat(e.target.value) || 0)}
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <div className="w-full">
                          <label className="block text-xs text-slate-500 mb-1">Total</label>
                          <div className="px-2 py-1.5 bg-slate-800/50 border border-slate-700 rounded text-amber-400 text-sm font-medium">
                            {formatCurrency(item.lineTotal)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex-1 mr-3">
                        <input
                          type="text"
                          value={item.notes}
                          onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                          placeholder="Line item notes..."
                          className="w-full px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 pt-4 border-t border-slate-700">
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between text-slate-400">
                      <span>Subtotal</span>
                      <span className="text-slate-100">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-400">Discount</span>
                      <div className="flex items-center gap-2">
                        <select
                          value={formData.discountType}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                          className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm"
                        >
                          <option value="PERCENT">%</option>
                          <option value="FIXED">$</option>
                        </select>
                        <input
                          type="number"
                          value={formData.discountValue}
                          onChange={(e) => setFormData(prev => ({ ...prev, discountValue: parseFloat(e.target.value) || 0 }))}
                          min="0"
                          step="0.01"
                          className="w-20 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm"
                        />
                        <span className="text-green-400 w-24 text-right">-{formatCurrency(discountAmount)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-400">Tax Rate</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.taxRate}
                          onChange={(e) => setFormData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                          min="0"
                          step="0.01"
                          className="w-20 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm"
                        />
                        <span className="text-slate-400">%</span>
                        <span className="text-slate-100 w-24 text-right">{formatCurrency(taxAmount)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-400">Delivery Fee</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.deliveryFee}
                          onChange={(e) => setFormData(prev => ({ ...prev, deliveryFee: parseFloat(e.target.value) || 0 }))}
                          min="0"
                          step="0.01"
                          className="w-32 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm"
                        />
                        <span className="w-24"></span>
                      </div>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-amber-500 font-bold">
                      <span className="text-slate-100 uppercase">Total</span>
                      <span className="text-amber-400 text-xl">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Terms & Conditions
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Delivery Terms
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryTerms: e.target.value }))}
                    placeholder="e.g., FOB Destination, Free Delivery within 50 miles"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Special Terms
                  </label>
                  <textarea
                    value={formData.specialTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, specialTerms: e.target.value }))}
                    rows={3}
                    placeholder="Any special terms or conditions..."
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Notes */}
          <div className="space-y-6">
            {/* Customer Notes */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Customer Notes
              </h3>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                placeholder="Notes visible to customer..."
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            {/* Internal Notes */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Internal Notes
              </h3>
              <textarea
                value={formData.internalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, internalNotes: e.target.value }))}
                rows={4}
                placeholder="Notes for internal use only..."
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            {/* Summary */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Quote Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Items</span>
                  <span className="text-slate-100">{formData.items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Subtotal</span>
                  <span className="text-slate-100">{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Discount</span>
                    <span className="text-green-400">-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                {taxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tax</span>
                    <span className="text-slate-100">{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                {formData.deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Delivery</span>
                    <span className="text-slate-100">{formatCurrency(formData.deliveryFee)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-slate-700">
                  <span className="text-slate-100 font-semibold">Total</span>
                  <span className="text-amber-400 font-bold text-lg">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <Link
            to="/sales/quotes"
            className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saveMutation.isPending ? 'Saving...' : isEdit ? 'Update Quote' : 'Create Quote'}
          </button>
        </div>

        {saveMutation.isError && (
          <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save quote'}
          </div>
        )}
      </form>
    </div>
  );
}
